import { Map, MapEvent } from "ol";
import React, { useEffect, useMemo, useState } from "react";
import {
  AisHistoryMapItem,
  ArrivalZoneMapItem,
  AtoNMapItem,
  BerthMapItem,
  DamMapItem,
  EquipmentDetailsMapItem,
  EventMapItem,
  FairwayMapItem,
  FerryMapItem,
  LuodeMapItem,
  MapItem,
  NauticalWarningMapItem,
  PoiMapItem,
  PortMapItem,
  PortServiceMapItem,
  ProjectShapePlanMapItem,
  ProjectShapeProgressMapItem,
  SeaMapItem,
  SeaSurveyMapItem,
  VesselMapItem,
  WaveMapItem,
  WindMapItem,
} from "../types/mapItems";
import { AisHistoryPopup } from "./AisHistoryPopup";
import { ArrivalZonePopup } from "./ArrivalZonePopup";
import { AtoNPopup } from "./AtoNPopup";
import { BerthPopup } from "./BerthPopup";
import { DamPopup } from "./DamPopup";
import { EventPopup } from "./EventPopup";
import { FairwayPopup } from "./FairwayPopup";
import { FerryPopup } from "./FerryPopup";
import { LuodePopup } from "./LuodePopup";
import { MeasurementPopup } from "./MeasurementPopup";
import { MultiplePopup } from "./MultiplePopup";
import { NauticalWarningPopup } from "./NauticalWarningPopup";
import { PoiPopup } from "./PoiPopup";
import { PortPopup } from "./PortPopup";
import { PortServicePopup } from "./PortServicePopup";
import { ProjectShapePlanPopup } from "./ProjectShapePlanPopup";
import { ProjectShapeProgressPopup } from "./ProjectShapeProgressPopup";
import { SeaPopup } from "./SeaPopup";
import { VehicleEquipmentPopup } from "./vehicleEquipmentPopup";
import { VesselPopup } from "./VesselPopups";
import { WavePopup } from "./WavePopup";
import { WindPopup } from "./WindPopups";

interface Props {
  map: Map;
  hoveredItem?: MapItem;
  selectedItem?: MapItem;
  multipleItems?: MapItem[];
  fleetPage?: boolean;
}

const MapPopups = ({
  map,
  hoveredItem,
  selectedItem,
  multipleItems,
  fleetPage,
}: Props): JSX.Element | null => {
  const [singleItem, setSingleItem] = useState<MapItem>();

  // Reset singleItem if clicked or hovered item changes
  useEffect(() => {
    setSingleItem(undefined);
  }, [selectedItem, hoveredItem]);

  // Also reset if overlay position changes
  useEffect(() => {
    const listener = (e: MapEvent) => {
      e.stopPropagation();
      setSingleItem(undefined);
    };
    const ol = map?.getOverlayById("popup");
    const listeners = ol?.getListeners("change:position");
    if (!listeners?.find((l) => l === listener)) {
      ol?.on("change:position", listener);
    }
    return () => {
      ol?.un("change:position", listener);
    };
  }, [map]);

  const clickedItem = useMemo(
    () => selectedItem ?? singleItem,
    [selectedItem, singleItem],
  );

  if (multipleItems && !singleItem)
    return (
      <MultiplePopup
        map={map}
        items={multipleItems}
        onItemSelect={(item) => setSingleItem(item)}
      />
    );

  const type = clickedItem?.type ?? hoveredItem?.type;

  if (!type || !map) return null;
  switch (type) {
    case "wind":
      return hoveredItem || clickedItem ? (
        <WindPopup
          map={map}
          hoverData={hoveredItem as WindMapItem}
          clickData={clickedItem as WindMapItem}
        />
      ) : null;
    case "wave":
      return hoveredItem || clickedItem ? (
        <WavePopup
          map={map}
          hoverData={hoveredItem as WaveMapItem}
          clickData={clickedItem as WaveMapItem}
        />
      ) : null;
    case "sea":
      return hoveredItem || clickedItem ? (
        <SeaPopup
          map={map}
          hoverData={hoveredItem as SeaMapItem}
          clickData={clickedItem as SeaMapItem}
        />
      ) : null;
    case "vessel":
      return hoveredItem || clickedItem ? (
        <VesselPopup
          map={map}
          hoverData={hoveredItem as VesselMapItem}
          clickData={clickedItem as VesselMapItem}
          fleet={fleetPage}
        />
      ) : null;
    case "event":
      return hoveredItem || clickedItem ? (
        <EventPopup
          map={map}
          hoverData={hoveredItem as EventMapItem}
          clickData={clickedItem as EventMapItem}
        />
      ) : null;

    case "port":
      return clickedItem ? (
        <PortPopup map={map} data={clickedItem as PortMapItem} />
      ) : null;

    case "fairway":
      return hoveredItem || clickedItem ? (
        <FairwayPopup
          map={map}
          hoverData={hoveredItem as FairwayMapItem}
          clickData={clickedItem as FairwayMapItem}
        />
      ) : null;

    case "measurement":
      return hoveredItem || clickedItem ? (
        <MeasurementPopup
          map={map}
          hoverData={hoveredItem as SeaSurveyMapItem}
          clickData={clickedItem as SeaSurveyMapItem}
        />
      ) : null;

    case "dam":
      return hoveredItem || clickedItem ? (
        <DamPopup
          map={map}
          hoverData={hoveredItem as DamMapItem}
          clickData={clickedItem as DamMapItem}
        />
      ) : null;
    case "ferry":
      return hoveredItem || clickedItem ? (
        <FerryPopup
          map={map}
          hoverData={hoveredItem as FerryMapItem}
          clickData={clickedItem as FerryMapItem}
        />
      ) : null;
    case "aton":
      return hoveredItem || clickedItem ? (
        <AtoNPopup
          map={map}
          hoverData={hoveredItem as AtoNMapItem}
          clickData={clickedItem as AtoNMapItem}
        />
      ) : null;
    case "berth":
      return hoveredItem || clickedItem ? (
        <BerthPopup
          map={map}
          hoverData={hoveredItem as BerthMapItem}
          clickData={clickedItem as BerthMapItem}
        />
      ) : null;
    case "port-service":
      return hoveredItem || clickedItem ? (
        <PortServicePopup
          map={map}
          hoverData={hoveredItem as PortServiceMapItem}
          clickData={clickedItem as PortServiceMapItem}
        />
      ) : null;
    case "nautical-warning":
      return hoveredItem || clickedItem ? (
        <NauticalWarningPopup
          map={map}
          hoverData={hoveredItem as NauticalWarningMapItem}
          clickData={clickedItem as NauticalWarningMapItem}
        />
      ) : null;
    case "condition":
      return hoveredItem || clickedItem ? (
        <LuodePopup
          map={map}
          hoverData={hoveredItem as LuodeMapItem}
          clickData={clickedItem as LuodeMapItem}
        />
      ) : null;
    // case "ptop-car":
    //   return hoveredItem || clickedItem ? (
    //     <PTOPCarPopup
    //       map={map}
    //       hoverData={hoveredItem as PTOPCarMapItem}
    //       clickData={clickedItem as PTOPCarMapItem}
    //     />
    //   ) : null;
    case "equipment":
      return hoveredItem || clickedItem ? (
        <VehicleEquipmentPopup
          map={map}
          hoverData={hoveredItem as EquipmentDetailsMapItem}
          clickData={clickedItem as EquipmentDetailsMapItem}
        />
      ) : null;
    case "ais-vessel-history-arrow":
      return <AisHistoryPopup data={hoveredItem as AisHistoryMapItem} />;
    case "project-shape-progress":
      return (
        <ProjectShapeProgressPopup
          map={map}
          data={clickedItem as ProjectShapeProgressMapItem}
        />
      );
    case "project-shape-plan":
      return (
        <ProjectShapePlanPopup
          map={map}
          data={clickedItem as ProjectShapePlanMapItem}
        />
      );
    case "poi-item":
      return <PoiPopup map={map} data={clickedItem as PoiMapItem} />;
    case "arrival-zone":
      return (
        <ArrivalZonePopup
          map={map}
          hoverData={hoveredItem as ArrivalZoneMapItem}
          clickData={clickedItem as ArrivalZoneMapItem}
        />
      );
    default:
      return null;
  }
};

export default MapPopups;
